// covered-by: frontends/carrie/src/routes/ask-puntt/hooks/__tests__/useAskPunttDesktopNotifications.test.ts
import {
  NotificationType,
  type Notification,
} from "@mg/schemas/src/commons/user";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { useUiPrefs } from "./useUiPrefs";

import { canAccessAskPuntt } from "../../../utils/auth";
import { useAskPunttAllChats } from "../../../utils/queries/askPuntt";
import { useAskPunttMentionNotificationsProfile } from "../../../utils/queries/userProfile";
import {
  ASK_PUNTT_UNREAD_HIDDEN_POLL_MS,
  buildUnreadChatSnapshots,
  findNewlyUnreadChats,
  getDesktopNotificationPermission,
  isDesktopNotificationSupported,
  showAskPunttChatDesktopNotification,
  type UnreadChatSnapshot,
  resolveActiveAskPunttChatId,
} from "../utils/askPunttDesktopNotifications";

function mentionNotificationKey(notification: Notification): string {
  return [
    String(notification.createdAt),
    String(notification.reference ?? ""),
    notification.message ?? "",
    notification.ctaLink ?? "",
  ].join("\u001f");
}

function mentionNotificationTarget(ctaLink: string): {
  chatId: string;
  projectId?: string;
} | null {
  const pathname = new URL(ctaLink, window.location.origin).pathname;
  const askPunttIndex = pathname.indexOf("/ask-puntt/");
  if (askPunttIndex < 0) return null;
  const askPunttPath = pathname.slice(askPunttIndex);

  const projectChatMatch = askPunttPath.match(
    /^\/ask-puntt\/projects\/([^/]+)\/chats\/([^/]+)(?:\/|$)/,
  );
  if (projectChatMatch?.[1] && projectChatMatch[2]) {
    return {
      projectId: projectChatMatch[1],
      chatId: projectChatMatch[2],
    };
  }

  const standaloneMatch = askPunttPath.match(/^\/ask-puntt\/([^/]+)(?:\/|$)/);
  return standaloneMatch?.[1] ? { chatId: standaloneMatch[1] } : null;
}

/**
 * Polls chat lists while the tab is hidden and raises native Notification
 * toasts when a background chat becomes unread. Opt-in via ui prefs.
 */
export function useAskPunttDesktopNotifications(): void {
  const navigate = useNavigate();
  const { prefs } = useUiPrefs();
  const enabled = prefs.askPunttDesktopNotifications === true;

  const [, setVisibilityTick] = useState(0);

  const location = useRouterState({
    select: (state) => ({
      pathname: state.location.pathname,
      search: state.location.search as Record<string, unknown>,
    }),
  });
  const activeChatId = resolveActiveAskPunttChatId(location);

  const previousSnapshotsRef = useRef<Map<string, UnreadChatSnapshot>>(
    new Map(),
  );
  const lastNotifiedAtByChatRef = useRef<Map<string, string>>(new Map());
  const hasSeededRef = useRef(false);
  const seenMentionKeysRef = useRef<Set<string>>(new Set());
  const hasSeededMentionsRef = useRef(false);
  const mentionPollingStartedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVisibilityChange = () => {
      setVisibilityTick((tick) => tick + 1);
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  const documentHidden =
    typeof document !== "undefined" && document.visibilityState === "hidden";
  const permission = getDesktopNotificationPermission();
  const canNotify =
    enabled &&
    permission === "granted" &&
    canAccessAskPuntt() &&
    isDesktopNotificationSupported();
  const shouldPoll = canNotify && documentHidden;

  useEffect(() => {
    if (!shouldPoll) {
      hasSeededRef.current = false;
    }
  }, [shouldPoll]);

  const { data } = useAskPunttAllChats({
    scope: "all",
    enabled: shouldPoll,
    refetchInterval: shouldPoll ? ASK_PUNTT_UNREAD_HIDDEN_POLL_MS : false,
  });
  const { data: profile } = useAskPunttMentionNotificationsProfile({
    enabled: shouldPoll,
    refetchInterval: shouldPoll ? ASK_PUNTT_UNREAD_HIDDEN_POLL_MS : false,
    refetchIntervalInBackground: true,
  });

  useEffect(() => {
    if (shouldPoll) {
      mentionPollingStartedAtRef.current ??= Date.now();
      return;
    }
    mentionPollingStartedAtRef.current = null;
    hasSeededMentionsRef.current = false;
    seenMentionKeysRef.current.clear();
  }, [shouldPoll]);

  useEffect(() => {
    if (!shouldPoll || profile?.notifications == null) return;

    const mentions = profile.notifications.filter(
      (notification) => notification.type === NotificationType.PROJECT_MENTION,
    );
    const isInitialBatch = !hasSeededMentionsRef.current;
    hasSeededMentionsRef.current = true;

    for (const notification of [...mentions].reverse()) {
      const key = mentionNotificationKey(notification);
      if (seenMentionKeysRef.current.has(key)) continue;
      seenMentionKeysRef.current.add(key);
      if (isInitialBatch) {
        const createdAt = new Date(notification.createdAt).getTime();
        const startedAt = mentionPollingStartedAtRef.current;
        if (
          startedAt == null ||
          !Number.isFinite(createdAt) ||
          createdAt < startedAt
        ) {
          continue;
        }
      }
      if (!notification.ctaLink) continue;
      const target = mentionNotificationTarget(notification.ctaLink);
      if (!target) continue;

      const snapshot: UnreadChatSnapshot = {
        chatId: target.chatId,
        projectId: target.projectId,
        title: notification.message ?? "You were mentioned in Ask Puntt",
        hasUnread: true,
        lastMessageAt: String(notification.createdAt),
      };
      showAskPunttChatDesktopNotification(snapshot, (selected) => {
        if (selected.projectId) {
          void navigate({
            to: "/ask-puntt/projects/$projectId",
            params: { projectId: selected.projectId },
            search: { tab: "chats", chatId: selected.chatId },
          });
          return;
        }
        void navigate({
          to: "/ask-puntt/$chatId",
          params: { chatId: selected.chatId },
        });
      });
      if (snapshot.lastMessageAt != null) {
        lastNotifiedAtByChatRef.current.set(
          snapshot.chatId,
          snapshot.lastMessageAt,
        );
      }
    }
  }, [navigate, profile?.notifications, shouldPoll]);

  useEffect(() => {
    if (!shouldPoll || data?.chats == null) return;

    const nextSnapshots = buildUnreadChatSnapshots(data.chats);

    if (!hasSeededRef.current) {
      for (const snapshot of Array.from(nextSnapshots.values())) {
        if (snapshot.hasUnread && snapshot.lastMessageAt != null) {
          lastNotifiedAtByChatRef.current.set(
            snapshot.chatId,
            snapshot.lastMessageAt,
          );
        }
      }
      previousSnapshotsRef.current = nextSnapshots;
      hasSeededRef.current = true;
      return;
    }

    const newlyUnread = findNewlyUnreadChats(
      previousSnapshotsRef.current,
      nextSnapshots,
      {
        activeChatId,
        lastNotifiedAtByChat: lastNotifiedAtByChatRef.current,
      },
    );

    for (const snapshot of newlyUnread) {
      showAskPunttChatDesktopNotification(snapshot, (target) => {
        if (target.projectId) {
          void navigate({
            to: "/ask-puntt/projects/$projectId",
            params: { projectId: target.projectId },
            search: { tab: "chats", chatId: target.chatId },
          });
          return;
        }
        void navigate({
          to: "/ask-puntt/$chatId",
          params: { chatId: target.chatId },
        });
      });
      if (snapshot.lastMessageAt != null) {
        lastNotifiedAtByChatRef.current.set(
          snapshot.chatId,
          snapshot.lastMessageAt,
        );
      }
    }

    previousSnapshotsRef.current = nextSnapshots;
  }, [activeChatId, data, navigate, shouldPoll]);
}
